import {
	section, header, h2, ul, li, button, span, div, input
} from 'iblokz-snabbdom-helpers';

import audioPlayerMod from '../../comp/media/audio';
import {resolveContent} from '../static';

const audioPlayer = audioPlayerMod.default || audioPlayerMod;

const ROW_WAVE_HEIGHT = 63;

const formatTime = (sec) => {
	if (!Number.isFinite(sec) || sec < 0) {
		return '0:00';
	}
	const s = Math.floor(sec);
	const m = Math.floor(s / 60);
	const r = s % 60;
	return `${m}:${r < 10 ? `0${r}` : r}`;
};

const rootOf = (el) =>
	(el && el.closest && el.closest('.widget-track-list')) || null;

const mediaElAt = (root, index) => {
	if (!root || index < 0) {
		return null;
	}
	const rows = root.querySelectorAll('.widget-track-list-row');
	return (rows[index] && rows[index].querySelector('.media-player')) || null;
};

const playerAt = (root, index) => {
	const el = mediaElAt(root, index);
	if (!el) {
		return null;
	}
	if (el.__ws) {
		return {kind: 'ws', api: el.__ws, el};
	}
	if (el.__sc) {
		return {kind: 'sc', api: el.__sc, el};
	}
	if (el.__mc) {
		return {kind: 'mc', api: el.__mc, el};
	}
	return null;
};

const activePlayer = (root) => {
	if (!root) {
		return null;
	}
	const el = root.querySelector('.widget-track-list-row.is-active .media-player');
	if (!el) {
		return null;
	}
	if (el.__ws) {
		return {kind: 'ws', api: el.__ws, el};
	}
	if (el.__sc) {
		return {kind: 'sc', api: el.__sc, el};
	}
	if (el.__mc) {
		return {kind: 'mc', api: el.__mc, el};
	}
	return null;
};

const resetPlayer = (player) => {
	if (!player) {
		return;
	}
	if (player.kind === 'ws') {
		if (typeof player.api.setTime === 'function') {
			player.api.setTime(0);
		} else {
			const media = player.api.getMediaElement && player.api.getMediaElement();
			if (media) {
				media.currentTime = 0;
			}
		}
		return;
	}
	if (player.kind === 'sc' && typeof player.api.seekTo === 'function') {
		player.api.seekTo(0);
	}
	if (player.kind === 'mc' && typeof player.api.seek === 'function') {
		player.api.seek(0).catch(() => {});
	}
};

const setPlayhead = (player, on) => {
	if (!player || player.kind !== 'ws' || typeof player.api.setOptions !== 'function') {
		return;
	}
	player.api.setOptions({
		cursorWidth: on ? 1 : 0,
		cursorColor: on ? '#111' : 'transparent',
		progressColor: on ? '#333' : '#8a8a8a'
	});
};

const pausePlayer = (player) => {
	if (!player || !player.api) {
		return;
	}
	const result = player.api.pause && player.api.pause();
	if (result && typeof result.catch === 'function') {
		result.catch(() => {});
	}
};

const waveRatioFromEvent = (ev, el) => {
	const rect = el.getBoundingClientRect();
	if (!rect.width) {
		return 0;
	}
	return Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
};

const withScReady = (el, fn) => {
	if (!el || !el.__sc) {
		return false;
	}
	const run = () => {
		fn(el.__sc);
	};
	if (el.__scReady) {
		run();
		return true;
	}
	const Widget = typeof window !== 'undefined' && window.SC && window.SC.Widget;
	if (Widget && Widget.Events) {
		el.__sc.bind(Widget.Events.READY, run);
	}
	try {
		run();
	} catch (err) {
		// wait for READY
	}
	return true;
};

const withMcReady = (el, fn) => {
	if (!el || !el.__mc) {
		return false;
	}
	const run = (api) => {
		fn(api || el.__mc);
	};
	if (el.__mcReady && typeof el.__mc.play === 'function') {
		run(el.__mc);
		return true;
	}
	if (el.__mc.ready && typeof el.__mc.ready.then === 'function') {
		el.__mc.ready.then(api => {
			el.__mc = api || el.__mc;
			el.__mcReady = true;
			run(el.__mc);
		}).catch(() => {});
		return true;
	}
	return false;
};

const seekAt = (root, index, ratio) => {
	const el = mediaElAt(root, index);
	if (!el || ratio == null) {
		return;
	}
	const r = Math.max(0, Math.min(1, Number(ratio) || 0));
	if (el.__ws) {
		const ws = el.__ws;
		const dur = typeof ws.getDuration === 'function' ? ws.getDuration() : 0;
		if (dur > 0 && typeof ws.setTime === 'function') {
			ws.setTime(r * dur);
		}
		return;
	}
	if (el.__mc) {
		withMcReady(el, (mc) => {
			if (typeof mc.getDuration !== 'function' || typeof mc.seek !== 'function') {
				return;
			}
			mc.getDuration().then(durationSec => {
				if (durationSec > 0) {
					mc.seek(r * durationSec);
				}
			}).catch(() => {});
		});
		return;
	}
	withScReady(el, (sc) => {
		if (typeof sc.getDuration !== 'function' || typeof sc.seekTo !== 'function') {
			return;
		}
		sc.getDuration((durationMs) => {
			if (durationMs > 0) {
				sc.seekTo(r * durationMs);
			}
		});
	});
};

/** Play row player; optional seekRatio (0–1). SC seeks while paused, then plays. */
const playAt = (root, index, setPaused, seekRatio = null) => {
	const el = mediaElAt(root, index);
	if (!el) {
		return;
	}
	const markPlaying = () => {
		if (typeof setPaused === 'function') {
			setPaused(false);
		}
	};
	if (el.__ws) {
		const ws = el.__ws;
		const go = () => {
			if (seekRatio != null) {
				const dur = typeof ws.getDuration === 'function' ? ws.getDuration() : 0;
				if (dur > 0 && typeof ws.setTime === 'function') {
					ws.setTime(Math.max(0, Math.min(1, seekRatio)) * dur);
				}
			}
			ws.play();
			markPlaying();
		};
		if (typeof ws.getDuration === 'function' && ws.getDuration() > 0) {
			go();
		} else if (typeof ws.once === 'function') {
			ws.once('ready', go);
		} else {
			go();
		}
		return;
	}

	const trySc = () => {
		if (!el.__sc) {
			return false;
		}
		const start = () => {
			el.__sc.play();
			markPlaying();
		};
		const run = () => {
			if (seekRatio == null
				|| typeof el.__sc.getDuration !== 'function'
				|| typeof el.__sc.seekTo !== 'function') {
				start();
				return;
			}
			const r = Math.max(0, Math.min(1, seekRatio));
			el.__sc.getDuration((durationMs) => {
				if (durationMs > 0) {
					el.__sc.seekTo(r * durationMs);
				}
				// Let the paused seek land before starting playback
				setTimeout(start, 40);
			});
		};
		if (el.__scReady) {
			run();
			return true;
		}
		const Widget = typeof window !== 'undefined' && window.SC && window.SC.Widget;
		if (Widget && Widget.Events) {
			el.__sc.bind(Widget.Events.READY, run);
		}
		try {
			run();
		} catch (err) {
			// ignore until READY
		}
		return true;
	};

	const tryMc = () => {
		if (!el.__mc) {
			return false;
		}
		const start = (mc) => {
			if (!mc || typeof mc.play !== 'function') {
				el.__mcPendingPlay = true;
				if (typeof console !== 'undefined') {
					console.log('[mtbg:mixcloud] play() deferred — API not ready');
				}
				return;
			}
			if (typeof console !== 'undefined') {
				console.log('[mtbg:mixcloud] parent play()', {
					unlocked: Boolean(el.__mcUnlocked),
					ready: Boolean(el.__mcReady)
				});
			}
			mc.play().then(() => {
				if (typeof mc.getIsPaused !== 'function') {
					return;
				}
				return mc.getIsPaused().then(paused => {
					if (typeof console !== 'undefined') {
						console.log('[mtbg:mixcloud] after parent play()', {paused, unlocked: Boolean(el.__mcUnlocked)});
					}
				});
			}).catch(err => {
				el.__mcPendingPlay = true;
				if (typeof console !== 'undefined') {
					console.log('[mtbg:mixcloud] play() rejected', String(err));
				}
			});
		};
		const run = (mc) => {
			const api = mc || el.__mc;
			if (seekRatio == null
				|| typeof api.getDuration !== 'function'
				|| typeof api.seek !== 'function') {
				start(api);
				return;
			}
			const r = Math.max(0, Math.min(1, seekRatio));
			api.getDuration().then(durationSec => {
				if (durationSec > 0) {
					return api.seek(r * durationSec).then(() => start(api));
				}
				start(api);
			}).catch(() => start(api));
		};
		if (el.__mcReady && typeof el.__mc.play === 'function') {
			run(el.__mc);
			return true;
		}
		if (el.__mc.ready && typeof el.__mc.ready.then === 'function') {
			el.__mcPendingPlay = true;
			el.__mc.ready.then(api => {
				el.__mc = api || el.__mc;
				el.__mcReady = true;
				run(el.__mc);
			}).catch(() => {});
			return true;
		}
		return false;
	};

	if (trySc() || tryMc()) {
		return;
	}
	let n = 0;
	const id = setInterval(() => {
		if (trySc() || tryMc() || ++n > 50) {
			clearInterval(id);
		}
	}, 100);
};

const playPlayer = (player, setPaused) => {
	if (!player || !player.el) {
		return;
	}
	const root = player.el.closest('.widget-track-list');
	const rows = root ? root.querySelectorAll('.widget-track-list-row') : [];
	let index = -1;
	rows.forEach((row, i) => {
		if (row.contains(player.el)) {
			index = i;
		}
	});
	if (index >= 0) {
		playAt(root, index, setPaused);
	}
};

const isPlayerPlaying = (player) => {
	if (!player) {
		return false;
	}
	if (player.kind === 'ws') {
		return Boolean(player.api.isPlaying && player.api.isPlaying());
	}
	return false;
};

const leaveRows = (root, keepIndex = -1) => {
	if (!root) {
		return;
	}
	root.querySelectorAll('.widget-track-list-row').forEach((row, i) => {
		const el = row.querySelector('.media-player');
		if (!el) {
			return;
		}
		const player = el.__ws
			? {kind: 'ws', api: el.__ws, el}
			: (el.__sc
				? {kind: 'sc', api: el.__sc, el}
				: (el.__mc ? {kind: 'mc', api: el.__mc, el} : null));
		if (!player) {
			return;
		}
		pausePlayer(player);
		if (i !== keepIndex) {
			resetPlayer(player);
			setPlayhead(player, false);
		} else {
			setPlayhead(player, true);
		}
	});
};

const bindMediaEvents = (media, root) => {
	if (!media || media.__mtbgBound) {
		return;
	}
	media.__mtbgBound = true;
	media.addEventListener('play', () => {
		if (root && typeof root.__mtbgSetPaused === 'function') {
			root.__mtbgSetPaused(false);
		}
	});
	media.addEventListener('pause', () => {
		if (media.ended) {
			return;
		}
		if (root && typeof root.__mtbgSetPaused === 'function') {
			root.__mtbgSetPaused(true);
		}
	});
	media.addEventListener('ended', () => {
		if (root && typeof root.__mtbgOnEnded === 'function') {
			root.__mtbgOnEnded();
		} else if (root && typeof root.__mtbgSetPaused === 'function') {
			root.__mtbgSetPaused(true);
		}
	});
};

const getListVolume = (rootOrTransport) => {
	const root = rootOrTransport && rootOrTransport.classList
		&& rootOrTransport.classList.contains('widget-track-list')
		? rootOrTransport
		: (rootOrTransport && rootOrTransport.closest && rootOrTransport.closest('.widget-track-list'));
	if (!root) {
		return 100;
	}
	const v = Number(root.__mtbgVolume);
	return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 100;
};

const setListVolume = (rootOrTransport, value) => {
	const root = rootOrTransport && rootOrTransport.classList
		&& rootOrTransport.classList.contains('widget-track-list')
		? rootOrTransport
		: (rootOrTransport && rootOrTransport.closest && rootOrTransport.closest('.widget-track-list'));
	if (!root) {
		return 100;
	}
	const v = Math.max(0, Math.min(100, Number(value)));
	root.__mtbgVolume = Number.isFinite(v) ? v : 100;
	return root.__mtbgVolume;
};

const applyVolumeToPlayer = (player, volume100) => {
	if (!player || !player.api) {
		return;
	}
	const v = Math.max(0, Math.min(100, Number(volume100)));
	if (player.kind === 'sc' && typeof player.api.setVolume === 'function') {
		player.api.setVolume(v);
		return;
	}
	if (player.kind === 'mc' && typeof player.api.setVolume === 'function') {
		player.api.setVolume(v / 100).catch(() => {});
		return;
	}
	if (player.kind === 'ws') {
		const media = player.api.getMediaElement && player.api.getMediaElement();
		if (media) {
			media.volume = v / 100;
		}
	}
};

/** Park active WaveSurfer media, or wire SC/Mixcloud seek+volume into the transport. */
const stopEmbedTransport = (transportEl) => {
	if (!transportEl) {
		return;
	}
	if (typeof transportEl.__mtbgEmbedCleanup === 'function') {
		transportEl.__mtbgEmbedCleanup();
		transportEl.__mtbgEmbedCleanup = null;
	}
	transportEl.__mtbgEmbedKey = null;
};

const updateEmbedSeekUi = (transportEl, positionSec, durationSec) => {
	if (!transportEl || transportEl.__mtbgSeeking) {
		return;
	}
	const seek = transportEl.querySelector('.widget-track-list-seek');
	const time = transportEl.querySelector('.widget-track-list-time');
	if (time) {
		time.textContent = `${formatTime(positionSec)} / ${formatTime(durationSec)}`;
	}
	if (seek && durationSec > 0) {
		const pct = Math.max(0, Math.min(100, (positionSec / durationSec) * 100));
		seek.value = String(Math.round((positionSec / durationSec) * 1000));
		seek.style.setProperty('--mtbg-fill', `${pct}%`);
	} else if (seek) {
		seek.value = '0';
		seek.style.setProperty('--mtbg-fill', '0%');
	}
};

const wireEmbedTransport = (transportEl, player) => {
	const seek = transportEl.querySelector('.widget-track-list-seek');
	const volume = transportEl.querySelector('.widget-track-list-volume');
	const time = transportEl.querySelector('.widget-track-list-time');
	if (!seek || !volume || !player || !player.api) {
		return;
	}

	const frame = player.el && player.el.querySelector && player.el.querySelector('iframe');
	const mediaHint = player.kind === 'ws'
		&& player.api.getMediaElement && player.api.getMediaElement();
	const key = `${player.kind}:${(frame && frame.src) || (mediaHint && mediaHint.src) || ''}`;
	if (transportEl.__mtbgEmbedKey === key && transportEl.__mtbgEmbedCleanup) {
		return;
	}
	stopEmbedTransport(transportEl);
	transportEl.__mtbgEmbedKey = key;

	let durationSec = 0;
	const cleanups = [];
	const listVolume = getListVolume(transportEl);
	volume.value = String(listVolume);
	volume.style.setProperty('--mtbg-fill', `${listVolume}%`);
	applyVolumeToPlayer(player, listVolume);

	const onSeekInput = () => {
		transportEl.__mtbgSeeking = true;
		const ratio = Math.max(0, Math.min(1, Number(seek.value) / 1000));
		seek.style.setProperty('--mtbg-fill', `${ratio * 100}%`);
		if (time && durationSec > 0) {
			time.textContent = `${formatTime(ratio * durationSec)} / ${formatTime(durationSec)}`;
		}
	};
	const onSeekCommit = () => {
		const ratio = Math.max(0, Math.min(1, Number(seek.value) / 1000));
		if (player.kind === 'sc' && typeof player.api.seekTo === 'function') {
			player.api.seekTo(ratio * durationSec * 1000);
		} else if (player.kind === 'mc' && typeof player.api.seek === 'function') {
			player.api.seek(ratio * durationSec).catch(() => {});
		} else if (player.kind === 'ws') {
			const ws = player.api;
			if (typeof ws.setTime === 'function') {
				ws.setTime(ratio * durationSec);
			} else {
				const media = ws.getMediaElement && ws.getMediaElement();
				if (media) {
					media.currentTime = ratio * durationSec;
				}
			}
		}
		transportEl.__mtbgSeeking = false;
	};
	seek.addEventListener('input', onSeekInput);
	seek.addEventListener('change', onSeekCommit);
	cleanups.push(() => {
		seek.removeEventListener('input', onSeekInput);
		seek.removeEventListener('change', onSeekCommit);
	});

	const onVolume = () => {
		const v = setListVolume(transportEl, volume.value);
		volume.style.setProperty('--mtbg-fill', `${v}%`);
		applyVolumeToPlayer(player, v);
	};
	volume.addEventListener('input', onVolume);
	volume.addEventListener('change', onVolume);
	cleanups.push(() => {
		volume.removeEventListener('input', onVolume);
		volume.removeEventListener('change', onVolume);
	});

	if (player.kind === 'sc') {
		const api = player.api;
		api.getDuration((ms) => {
			durationSec = (ms || 0) / 1000;
			updateEmbedSeekUi(transportEl, 0, durationSec);
			if (typeof api.getPosition === 'function') {
				api.getPosition((posMs) => {
					updateEmbedSeekUi(transportEl, (posMs || 0) / 1000, durationSec);
				});
			}
		});
		const Widget = typeof window !== 'undefined' && window.SC && window.SC.Widget;
		if (Widget && Widget.Events && Widget.Events.PLAY_PROGRESS) {
			const onProgress = (data) => {
				const ms = data && typeof data.currentPosition === 'number'
					? data.currentPosition
					: 0;
				updateEmbedSeekUi(transportEl, ms / 1000, durationSec);
			};
			api.bind(Widget.Events.PLAY_PROGRESS, onProgress);
			cleanups.push(() => {
				if (typeof api.unbind === 'function') {
					api.unbind(Widget.Events.PLAY_PROGRESS, onProgress);
				}
			});
		}
	} else if (player.kind === 'mc') {
		const api = player.api;
		const refresh = () => {
			Promise.all([
				typeof api.getDuration === 'function' ? api.getDuration() : Promise.resolve(0),
				typeof api.getPosition === 'function' ? api.getPosition() : Promise.resolve(0)
			]).then(([d, p]) => {
				durationSec = Number(d) || 0;
				updateEmbedSeekUi(transportEl, Number(p) || 0, durationSec);
			}).catch(() => {});
		};
		refresh();
		if (api.events && api.events.progress && typeof api.events.progress.on === 'function') {
			const onProgress = (position, duration) => {
				durationSec = Number(duration) || durationSec;
				updateEmbedSeekUi(transportEl, Number(position) || 0, durationSec);
			};
			api.events.progress.on(onProgress);
			cleanups.push(() => {
				if (api.events.progress.off) {
					api.events.progress.off(onProgress);
				}
			});
		} else {
			const id = setInterval(refresh, 500);
			cleanups.push(() => clearInterval(id));
		}
	} else if (player.kind === 'ws') {
		const ws = player.api;
		const media = ws.getMediaElement && ws.getMediaElement();
		const root = transportEl.closest('.widget-track-list');
		const refresh = () => {
			durationSec = typeof ws.getDuration === 'function' ? ws.getDuration() : 0;
			const pos = typeof ws.getCurrentTime === 'function'
				? ws.getCurrentTime()
				: ((media && media.currentTime) || 0);
			updateEmbedSeekUi(transportEl, pos, durationSec);
		};
		refresh();
		if (media) {
			media.controls = false;
			bindMediaEvents(media, root);
			media.addEventListener('timeupdate', refresh);
			media.addEventListener('loadedmetadata', refresh);
			media.addEventListener('durationchange', refresh);
			cleanups.push(() => {
				media.removeEventListener('timeupdate', refresh);
				media.removeEventListener('loadedmetadata', refresh);
				media.removeEventListener('durationchange', refresh);
			});
		}
		if (typeof ws.on === 'function') {
			ws.on('audioprocess', refresh);
			ws.on('seeking', refresh);
			ws.on('ready', refresh);
			cleanups.push(() => {
				if (typeof ws.un === 'function') {
					ws.un('audioprocess', refresh);
					ws.un('seeking', refresh);
					ws.un('ready', refresh);
				}
			});
		}
	}

	transportEl.__mtbgEmbedCleanup = () => {
		transportEl.__mtbgSeeking = false;
		cleanups.forEach(fn => fn());
	};
};

const returnParkedAudio = (root, transportEl) => {
	Array.from(transportEl.querySelectorAll('audio')).forEach((audio) => {
		const wave = root.querySelector('.media-player.wavesurfer');
		if (wave && audio.parentElement === transportEl) {
			audio.controls = false;
			wave.appendChild(audio);
		}
	});
};

const syncTransport = (transportEl) => {
	const root = transportEl && transportEl.closest('.widget-track-list');
	if (!root || !transportEl) {
		return;
	}
	returnParkedAudio(root, transportEl);
	const player = activePlayer(root);
	const ui = transportEl.querySelector('.widget-track-list-embed-transport');
	if (player && ui && (player.kind === 'sc' || player.kind === 'mc' || player.kind === 'ws')) {
		wireEmbedTransport(transportEl, player);
		return;
	}
	stopEmbedTransport(transportEl);
};

const CONTROLS = [
	{icon: 'random', action: 'shuffle'},
	{icon: 'step-backward', action: 'previous'},
	{icon: 'play', action: 'play'},
	{icon: 'step-forward', action: 'next'},
	{icon: 'repeat', action: 'repeat'}
];

const REPEAT_CYCLE = ['off', 'all', 'one'];

const normalizeRepeat = (value) => {
	if (value === 'all' || value === 'one') {
		return value;
	}
	// legacy boolean: on meant loop playlist
	if (value === true) {
		return 'all';
	}
	return 'off';
};

const nextRepeat = (value) => {
	const cur = normalizeRepeat(value);
	const i = Math.max(0, REPEAT_CYCLE.indexOf(cur));
	return REPEAT_CYCLE[(i + 1) % REPEAT_CYCLE.length];
};

const pickIndex = (listLength, from, delta, shuffle) => {
	if (listLength <= 0) {
		return -1;
	}
	if (listLength === 1) {
		return 0;
	}
	if (shuffle) {
		let i = from;
		while (i === from) {
			i = Math.floor(Math.random() * listLength);
		}
		return i;
	}
	const base = from < 0 ? 0 : from;
	return (base + delta + listLength) % listLength;
};

/**
 * Track list — header · transport · rows.
 * WaveSurfer owns seek on click; we only move play/pause between rows.
 *
 * Important: keep `li` / play `button` selectors stable — putting `.is-active` /
 * `.fa-pause` in the sel string makes snabbdom destroy+recreate the node (and
 * WaveSurfer with it) on every selection change.
 */
export default ({
	node,
	i18n,
	list = [],
	activeIndex = 0,
	paused = true,
	shuffle = false,
	repeat = 'off',
	onSelect,
	onPlayback
}) => {
	if (!list.length) {
		return [];
	}

	const title = resolveContent(
		(node && (node.title || node.content)) || 'i18n.widget.trackList',
		i18n || {}
	) || 'Тракове';
	const select = typeof onSelect === 'function' ? onSelect : () => {};
	const patchPlayback = (patch) => {
		if (typeof onPlayback === 'function') {
			onPlayback(patch);
		}
	};
	const setPaused = (value) => patchPlayback({paused: value});
	const isPlaying = paused === false;
	const repeatMode = normalizeRepeat(repeat);

	const selectRow = (root, index, {play = false, seekRatio = null} = {}) => {
		leaveRows(root, index);
		if (play) {
			// one patch so paused:false cannot clobber a just-updated track
			patchPlayback({track: index, paused: false});
		} else {
			select(index);
		}
		requestAnimationFrame(() => {
			if (play) {
				playAt(root, index, setPaused, seekRatio);
			} else if (seekRatio != null) {
				seekAt(root, index, seekRatio);
			}
		});
	};

	const step = (delta, el) => {
		if (!list.length) {
			return;
		}
		const i = pickIndex(list.length, activeIndex, delta, shuffle);
		selectRow(rootOf(el), i, {play: true});
	};

	const onTrackEnded = (root) => {
		if (!root) {
			return;
		}
		const ctx = root.__mtbgCtx || {};
		const idx = typeof ctx.activeIndex === 'number' ? ctx.activeIndex : 0;
		const len = typeof ctx.listLength === 'number' ? ctx.listLength : 0;
		const mode = normalizeRepeat(ctx.repeat);
		const doShuffle = Boolean(ctx.shuffle);
		if (mode === 'one') {
			const player = activePlayer(root);
			resetPlayer(player);
			playAt(root, idx, setPaused);
			return;
		}
		// off: stop after last; all / shuffle: wrap via pickIndex
		if (mode === 'off' && !doShuffle && idx >= len - 1) {
			setPaused(true);
			return;
		}
		const i = pickIndex(len, idx, 1, doShuffle);
		selectRow(root, i, {play: true});
	};

	const bindRootCtx = (elm) => {
		if (!elm) {
			return;
		}
		elm.__mtbgSetPaused = setPaused;
		elm.__mtbgOnEnded = () => onTrackEnded(elm);
		elm.__mtbgCtx = {
			activeIndex,
			listLength: list.length,
			shuffle,
			repeat: repeatMode
		};
	};

	return section('.widget.widget-track-list', {
		hook: {
			insert: (vnode) => bindRootCtx(vnode.elm),
			update: (oldVnode, vnode) => bindRootCtx(vnode.elm)
		},
		on: {
			mtbgPlayback: (ev) => {
				if (!ev || !ev.detail) {
					return;
				}
				const root = rootOf(ev.currentTarget) || ev.currentTarget;
				if (ev.detail.ended) {
					onTrackEnded(root);
					return;
				}
				if (typeof ev.detail.paused === 'boolean') {
					setPaused(ev.detail.paused);
				}
			}
		}
	}, [
		header('.widget-header.widget-track-list-header', [
			h2('.widget-title', title),
			ul('.widget-track-list-controls', CONTROLS.map((control) => {
				const icon = control.action === 'play' && isPlaying
					? 'pause'
					: control.icon;
				const isOn = (control.action === 'shuffle' && shuffle)
					|| (control.action === 'repeat' && repeatMode !== 'off')
					|| (control.action === 'play' && isPlaying);
				const repeatTitle = repeatMode === 'one'
					? 'repeat one'
					: (repeatMode === 'all' ? 'repeat all' : 'repeat off');
				return li([button('.fa', {
					class: {
						[`fa-${icon}`]: true,
						'is-on': isOn,
						'is-repeat-one': control.action === 'repeat' && repeatMode === 'one'
					},
					attrs: {
						type: 'button',
						title: control.action === 'repeat' ? repeatTitle : control.action
					},
					on: {
						click: (ev) => {
							const root = rootOf(ev.currentTarget);
							const player = activePlayer(root);
							if (control.action === 'shuffle') {
								patchPlayback({shuffle: !shuffle});
								return;
							}
							if (control.action === 'repeat') {
								patchPlayback({repeat: nextRepeat(repeatMode)});
								return;
							}
							if (control.action === 'play') {
								if (!player) {
									if (activeIndex >= 0) {
										playAt(root, activeIndex, setPaused);
									}
									return;
								}
								if (isPlaying || isPlayerPlaying(player)) {
									pausePlayer(player);
									setPaused(true);
								} else {
									playPlayer(player, setPaused);
								}
								return;
							}
							if (control.action === 'previous') {
								step(-1, ev.currentTarget);
							} else if (control.action === 'next') {
								step(1, ev.currentTarget);
							}
						}
					}
				})]);
			}))
		]),
		div('.widget-track-list-transport.is-embed', {
			hook: {
				insert: (vnode) => {
					const onWsReady = () => syncTransport(vnode.elm);
					vnode.elm.__mtbgOnWsReady = onWsReady;
					vnode.elm.addEventListener('mtbgWsReady', onWsReady);
					syncTransport(vnode.elm);
				},
				postpatch: (oldVnode, vnode) => {
					vnode.elm.__mtbgOnWsReady = oldVnode.elm.__mtbgOnWsReady;
					syncTransport(vnode.elm);
				},
				destroy: (vnode) => {
					if (vnode.elm.__mtbgOnWsReady) {
						vnode.elm.removeEventListener('mtbgWsReady', vnode.elm.__mtbgOnWsReady);
						delete vnode.elm.__mtbgOnWsReady;
					}
					stopEmbedTransport(vnode.elm);
				}
			}
		}, [
			div('.widget-track-list-embed-transport', [
				span('.widget-track-list-time', '0:00 / 0:00'),
				input('.widget-track-list-seek', {
					attrs: {
						type: 'range',
						min: '0',
						max: '1000',
						value: '0',
						step: '1',
						'aria-label': 'Seek'
					}
				}),
				div('.widget-track-list-vol', [
					input('.widget-track-list-volume', {
						attrs: {
							type: 'range',
							min: '0',
							max: '100',
							value: '100',
							step: '1',
							'aria-label': 'Volume'
						}
					}),
					span('.widget-track-list-vol-icon.fa.fa-volume-up')
				])
			])
		]),
		ul('.widget-body.widget-track-list-rows', list.map((track, i) => {
			const active = i === activeIndex;
			const rowPlaying = active && isPlaying;
			const isSc = track.source === 'soundcloud'
				|| /soundcloud\.com/.test(track.url || '');
			const isMc = track.source === 'mixcloud'
				|| /mixcloud\.com/.test(track.url || '');
			return li('.widget-track-list-row', {
				key: track.url || String(i),
				class: {
					'is-active': active,
					'is-soundcloud': isSc,
					'is-mixcloud': isMc
				},
				on: {
					click: (ev) => {
						if (ev.target.closest && ev.target.closest('.widget-track-list-play')) {
							return;
						}
						if (ev.target.closest && ev.target.closest('.widget-track-list-sc-hit')) {
							return;
						}
						const root = rootOf(ev.currentTarget);
						// Row click selects + plays (global playback state)
						if (!active) {
							selectRow(root, i, {play: true});
							return;
						}
						if (!isPlaying) {
							playAt(root, i, setPaused);
						}
					}
				}
			}, [
				button('.widget-track-list-play.fa', {
					class: {
						'fa-play': !rowPlaying,
						'fa-pause': rowPlaying
					},
					attrs: {type: 'button'},
					on: {
						click: (ev) => {
							ev.stopPropagation();
							const root = rootOf(ev.currentTarget);
							if (active) {
								const player = playerAt(root, i);
								if (!player) {
									if (isPlaying) {
										setPaused(true);
									} else {
										playAt(root, i, setPaused);
									}
									return;
								}
								if (isPlaying || isPlayerPlaying(player)) {
									pausePlayer(player);
									setPaused(true);
								} else {
									playPlayer(player, setPaused);
								}
								return;
							}
							selectRow(root, i, {play: true});
						}
					}
				}),
				div('.widget-track-list-wave', [
					audioPlayer({
						track,
						height: ROW_WAVE_HEIGHT,
						mediaControls: false,
						interact: true
					}),
					// SC + Mixcloud: capture clicks above the embed for seek + play.
					// Mixcloud iframe is pointer-events:none so links never open a new tab.
					(isSc || isMc)
						? div('.widget-track-list-sc-hit', {
							on: {
								click: (ev) => {
									ev.stopPropagation();
									const root = rootOf(ev.currentTarget);
									const ratio = waveRatioFromEvent(ev, ev.currentTarget);
									if (!active) {
										selectRow(root, i, {
											play: true,
											seekRatio: ratio
										});
										return;
									}
									seekAt(root, i, ratio);
									if (!isPlaying) {
										playAt(root, i, setPaused, ratio);
									}
								}
							}
						})
						: '',
					span('.widget-track-list-meta', [
						isSc ? span('.widget-track-list-source.fa.fa-soundcloud') : '',
						isMc ? span('.widget-track-list-source.fa.fa-mixcloud') : '',
						span('.widget-track-list-title', track.title || track.url || 'track')
					])
				])
			]);
		}))
	]);
};
